import i18n from "@/i18n.ts";
import { ApiJobLevel } from "@/types/job-level.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Input, Modal, ModalProps, Stack, useModals } from "@skailer-com/uikit";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export interface JobLevelSaveData {
  title: string;
  gradeId: number;
  trackId: number;
}

export interface JobLevelsEditModalProps extends ModalProps {
  level?: ApiJobLevel;
  gradeId?: number;
  trackId?: number;
  onSave: (data: JobLevelSaveData) => void;
}

export function JobLevelsEditModal({
  level,
  gradeId,
  trackId,
  onSave,
  ...props
}: JobLevelsEditModalProps) {
  const { id } = { ...props };
  const { t } = useTranslation();
  const { close } = useModals();

  const isEdit = !!level?.id && level.id > 0;

  const effectiveGradeId = level?.grade.id ?? gradeId ?? 0;
  const effectiveTrackId = level?.track.id ?? trackId ?? 0;

  const levelSchema = z.object({
    title: z.string().min(1, i18n.t("errors:field_required")),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: "onSubmit",
    resolver: zodResolver(levelSchema),
    defaultValues: {
      title: level?.title ?? "",
    },
  });

  const onSubmit = handleSubmit((data) => {
    onSave({
      title: data.title,
      gradeId: effectiveGradeId,
      trackId: effectiveTrackId,
    });
    close(id);
  });

  return (
    <Modal
      {...props}
      title={t(isEdit ? "core:edit_job_level" : "core:create_job_level")}
      withCloser
      actions={
        <Stack>
          <Button type={"submit"} size={"lg"} onClick={onSubmit}>
            {t("common:save")}
          </Button>
        </Stack>
      }
    >
      <form onSubmit={onSubmit}>
        <Stack gap={"md"}>
          <Input
            label={t("common:title")}
            required={true}
            autoFocus={true}
            type="text"
            {...register("title")}
            error={errors.title?.message}
          />
        </Stack>
      </form>
    </Modal>
  );
}
